import PropTypes from 'prop-types';
import { push } from 'connected-react18-router';
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { List } from 'immutable';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import moment from 'moment';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import config from '../config';
import { Backlog } from '../components/Backlog';
import { loadUsers, loadBacklogs, changeUser, sort, fetchNextBacklogs } from '../actions/backlog';
import { openInternalLink } from '../utils/url';
import { isResearcher } from '../utils/checkPermissions';
import { showError } from '../utils/MessagePopup';
import { usePopupContext } from '../contexts/PopupContext';

/**
 * Backlog container component.
 */
const BacklogContainer = ({
  user,
  users,
  columnConfigs,
  loadUsers,
  loadBacklogs,
  changeUser,
  sort,
  fetchNextBacklogs,
  push,
  ...rest
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const [dateQuery, setDateQuery] = useState(moment());
  const gridApiRef = useRef(null);

  const hasAccess = () => {
    if (!user) return false;

    return !isResearcher(user.getIn(['roles', 0, 'slug'], null));
  };

  const getSorting = useCallback(columns => {
    const sortingCol = columns.find(column => column.get('sort', '').length > 0);

    if (sortingCol) {
      return `${sortingCol.get('sort') === 'asc' ? '' : '-'}${sortingCol.get('field')}`;
    }

    return null;
  }, []);

  const getSelectedUserIds = useCallback(userList => userList.filter(userItem => userItem.get('selected')).map(userItem => userItem.get('id')), []);

  useEffect(() => {
    if (!hasAccess()) {
      showError(openPopup, config.permisionError, () => {
        push('/');
        closePopup();
      });

      return;
    }

    loadUsers();
  }, []);

  useEffect(() => {
    if (!hasAccess()) return;

    const ids = getSelectedUserIds(users).toJS();
    const sorting = getSorting(columnConfigs);

    if (gridApiRef.current?.ensureIndexVisible) gridApiRef.current.ensureIndexVisible(0);
    loadBacklogs(ids, sorting, dateQuery.format('YYYY-MM-DD'));
  }, [users, columnConfigs]);

  const handleChangeUser = useCallback(
    event => {
      const { value, checked } = event.target;

      changeUser(parseInt(value, 10), checked);
    },
    [changeUser],
  );

  const handleMailingClick = useCallback(() => {
    openInternalLink('/mailing-history');
  }, []);

  const handleSendingEmailTemplatesClick = useCallback(() => {
    openInternalLink('/email-templates-management');
  }, []);

  const handleRowDoubleClick = useCallback(
    ({ data: { recordOwnerId, recordSubOwnerId, buyerId, projectId, activity, isEmail, isLetter } }) => {
      const beforeDate = moment(dateQuery).format('YYYY-MM-DD');

      openInternalLink(
        `/mailing-create?recordOwnerId=${recordOwnerId}&recordSubOwnerId=${recordSubOwnerId}&buyerId=${buyerId}&projectId=${projectId}&activity=${activity}&isEmail=${isEmail}&isLetter=${isLetter}&beforeDate=${beforeDate}`,
      );
    },
    [dateQuery],
  );

  const handleSort = useCallback(
    event => {
      if (event.length > 0) {
        sort(event[0].colId, event[0].sort);
      }
    },
    [sort],
  );

  const handleGridReady = useCallback(params => {
    gridApiRef.current = params.api;
  }, []);

  const handleReloadClick = useCallback(() => {
    const ids = getSelectedUserIds(users).toJS();
    const sorting = getSorting(columnConfigs);

    gridApiRef.current.ensureIndexVisible(0);
    loadBacklogs(ids, sorting, dateQuery.format('YYYY-MM-DD'));
  }, [users, columnConfigs, dateQuery, loadBacklogs, getSelectedUserIds, getSorting]);

  const handleOneOffApproachClick = useCallback(() => {
    openPopup('OneOffApproachPopup', { dateQuery, callback: handleReloadClick });
  }, [openPopup, dateQuery, handleReloadClick]);

  const handleDateChange = useCallback(
    event => {
      const ids = getSelectedUserIds(users).toJS();
      const sorting = getSorting(columnConfigs);

      setDateQuery(event.target.value);
      gridApiRef.current.ensureIndexVisible(0);
      loadBacklogs(ids, sorting, event.target.value.format('YYYY-MM-DD'));
    },
    [users, columnConfigs, loadBacklogs, getSelectedUserIds, getSorting],
  );

  const content = hasAccess() ? (
    <Backlog
      {...rest}
      columnConfigs={columnConfigs}
      dateQuery={dateQuery}
      onChangeUser={handleChangeUser}
      onDateChange={handleDateChange}
      onDoubleClick={handleRowDoubleClick}
      onGetNextPageData={handleGetNextPageData}
      onGridReady={handleGridReady}
      onMailingClick={handleMailingClick}
      onOneOffApproachClick={handleOneOffApproachClick}
      onReloadClick={handleReloadClick}
      onSendingEmailTemplatesClick={handleSendingEmailTemplatesClick}
      onSort={handleSort}
      users={users}
    />
  ) : null;

  // handleGetNextPageData depends on users/columnConfigs/dateQuery, defined after other handlers
  function handleGetNextPageData(page) {
    const ids = getSelectedUserIds(users).toJS();
    const sorting = getSorting(columnConfigs);
    const date = dateQuery.format('YYYY-MM-DD');

    if (ids.length) {
      fetchNextBacklogs(ids, page, sorting, date);
    }
  }

  return (
    <>
      <Helmet title="Backlog Page" />
      {content}
    </>
  );
};

BacklogContainer.propTypes = {
  backlogLoading: PropTypes.bool.isRequired,
  backlogs: PropTypes.instanceOf(List).isRequired,
  changeUser: PropTypes.func,
  columnConfigs: PropTypes.instanceOf(List).isRequired,
  fetchNextBacklogs: PropTypes.func,
  loadBacklogs: PropTypes.func,
  loadUsers: PropTypes.func,
  sort: PropTypes.func,
  userLoading: PropTypes.bool.isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = state => ({
  users: state.backlog.get('users'),
  backlogs: state.backlog.get('backlogs'),
  userLoading: state.backlog.get('userLoading'),
  backlogLoading: state.backlog.get('backlogLoading'),
  columnConfigs: state.backlog.get('columns'),
  totalPages: state.backlog.get('totalPages'),
  currentPage: state.backlog.get('currentPage'),
  user: state.auth.get('user', null),
});

export default connect(
  mapStateToProps,
  {
    loadBacklogs,
    loadUsers,
    changeUser,
    sort,
    fetchNextBacklogs,
    push,
  },
  mergeProps,
  connectOptions,
)(memo(BacklogContainer));
